/**
 * THE SHAPE OF ONE'S ANSWER, PARSED — never rendered as HTML (ask-one v9).
 *
 * One now answers as a lead sentence, then up to four bullets or numbered
 * steps, with **bold** for a date, a number or a name. This file turns that
 * text into a small tree the drawer renders as React elements. It is the ONLY
 * markup One's text can carry, and it is deliberately tiny:
 *
 *   - `- ` / `* ` / `• ` at the start of a line  → a bullet
 *   - `1. ` / `1) ` at the start of a line       → a numbered step
 *   - `**words**`                                → strong
 *   - `[words](#/route)`                         → a link, ONLY to a route in
 *                                                  the answer's own sources
 *   - anything else                              → a paragraph, as typed
 *
 * ── A LINK IS ONLY EVER TO SOMETHING ONE WAS GIVEN (HLP-37, 1 Nov) ─────────
 *
 * A model asked for links invents them: plausible routes that open nothing,
 * or a web address somebody then trusts because the hub showed it. So until
 * HLP-37 there was no link syntax at all. Now there is exactly one, under one
 * rule, enforced twice: `[words](#/route)` survives only when `route` is the
 * page of a help entry the answer CITED or a guide section a guides read
 * RETURNED - the server's `keepSourceLinks` reduces everything else to its
 * words, and `OneText` checks again against the answer's `sources` before it
 * draws an <a>. A bare https:// stays text. Nothing in the drawer passes One's
 * text to dangerouslySetInnerHTML; the suite reads both.
 *
 * Pure and exported so the suite can assert the parse without a DOM.
 */

export type OneSpan = { text: string; strong: boolean; href?: string }

export type OneBlock =
  | { kind: 'p'; spans: OneSpan[] }
  | { kind: 'ul'; items: OneSpan[][] }
  | { kind: 'ol'; items: OneSpan[][] }

const BULLET = /^\s*(?:[-*•])\s+(.*)$/
const STEP = /^\s*\d{1,2}[.)]\s+(.*)$/

/** `**bold**` and `[words](#/route)`. An unclosed `**` is text, not the start
 *  of a bold run; a link to anything but a `#/` route is its words (the
 *  caller still checks the route against the answer's sources). */
export function spansOf(line: string): OneSpan[] {
  const out: OneSpan[] = []
  const re = /\*\*([^*\n]+?)\*\*|\[([^\]\n]{1,120})\]\(([^)\s]{0,300})\)/g
  let at = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(line))) {
    if (m.index > at) out.push({ text: line.slice(at, m.index), strong: false })
    if (m[1] !== undefined) out.push({ text: m[1], strong: true })
    // SUP-2: an action page carries one short query (#/help?new=ticket); the
    // caller's set of the answer's sources decides which survive.
    else if (/^#\/[a-z0-9/_-]{0,120}(?:\?[a-z0-9=&_-]{1,60})?$/i.test(m[3])) out.push({ text: m[2], strong: false, href: m[3] })
    else out.push({ text: m[2], strong: false })
    at = m.index + m[0].length
  }
  if (at < line.length) out.push({ text: line.slice(at), strong: false })
  return out.length ? out : [{ text: '', strong: false }]
}

/** The routes an answer may link inline: the pages of the help entries it
 *  cited and the guide sections it read - the same set the server used. */
export function linkableRoutes(
  sources: ReadonlyArray<{ kind: string; route?: string; page?: string }> | undefined,
): Set<string> {
  const out = new Set<string>()
  for (const s of sources ?? []) {
    if ((s.kind === 'guide' || s.kind === 'guide_section') && typeof s.route === 'string') out.add(s.route)
    // SUP-2: a cited guide section's action page - the place to do it.
    if (s.kind === 'guide_section' && typeof s.page === 'string') out.add(s.page)
  }
  return out
}

export function parseOneText(text: string): OneBlock[] {
  const blocks: OneBlock[] = []
  // Paragraph lines that belong together (a soft-wrapped sentence) are joined
  // with a space; a blank line ends the paragraph.
  let para: string[] = []
  const flush = () => {
    if (para.length) blocks.push({ kind: 'p', spans: spansOf(para.join(' ')) })
    para = []
  }

  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd()
    const b = BULLET.exec(line)
    const s = b ? null : STEP.exec(line)
    if (b || s) {
      flush()
      const kind = b ? 'ul' : 'ol'
      const item = spansOf((b ?? s)![1].trim())
      const prev = blocks[blocks.length - 1]
      if (prev && prev.kind === kind) prev.items.push(item)
      else blocks.push(kind === 'ul' ? { kind: 'ul', items: [item] } : { kind: 'ol', items: [item] })
    } else if (line.trim() === '') {
      flush()
    } else {
      para.push(line.trim())
    }
  }
  flush()
  return blocks
}

/* ── where a guide entry points (HLP-37, 346) ──────────────────────────────
 *
 * Read from `platform_help_entry.route`, which the server sends on each cited
 * entry's source. GUIDE_SCREENS (HLP-36's stopgap, the page read off the KEY'S
 * PREFIX) is deleted. The rule it carried is now the column's: `hr_`,
 * `platform_` and `technical_` entries have NO route, because they are
 * instructions about where a question must NOT be answered and a button under
 * them would be a destination - HLP-17's rule, one step removed. The door to a
 * person is Help's, never a source link's. The label is lib/pages.ts's.
 */

/** A Wiki One item opens through #/item/:id, which already resolves a wiki id
 *  to `#/wiki?open=<id>` (Item.tsx). Ids are uuids; anything else gets no link. */
export function wikiHrefFor(contentId: string): string | null {
  return /^[0-9a-f-]{36}$/i.test(contentId) ? `#/item/${contentId}` : null
}
